import React, { useState, useEffect } from 'react';
import { appointmentsAPI } from '../services/api';

const CATEGORIES = ['Medical', 'Dental', 'School', 'Work', 'Personal', 'Other'];

const CATEGORY_COLORS = {
  Medical:  'var(--accent-teal)',
  Dental:   'var(--accent-teal)',
  School:   'var(--accent-teal)',
  Work:     'var(--accent-teal)',
  Personal: 'var(--accent-teal)',
  Other:    'var(--accent-teal)',
};

// mysql2 serialises DATETIME as UTC ISO strings ("...Z") even though the value
// was stored without timezone. Strip the Z so JS parses it as local time.
function parseLocalDt(dt) {
  if (!dt) return null;
  return new Date(dt.replace(' ', 'T').replace(/Z$/, ''));
}

function formatDateTime(dt) {
  if (!dt) return '';
  return parseLocalDt(dt).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function toInputDateTime(dt) {
  if (!dt) return '';
  const d = parseLocalDt(dt);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const EMPTY_FORM = {
  title: '', description: '', appointment_date: '',
  end_time: '', location: '', category: '', notes: '',
};

function AppointmentItem({ item, onEdit, onDelete, past }) {
  const color = CATEGORY_COLORS[item.category] || 'var(--text-muted)';
  return (
    <div className={`list-item${past ? ' completed' : ''}`}>
      <div style={{
        width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
        background: color, marginTop: 2,
      }} />
      <div className="list-item-content">
        <div className="list-item-title">
          {item.title}
          {item.category && (
            <span className="badge" style={{
              background: `${color}22`, color, border: `1px solid ${color}44`,
            }}>
              {item.category}
            </span>
          )}
        </div>
        <div className="list-item-subtitle">
          {formatDateTime(item.appointment_date)}
          {item.end_time && ` — ${formatDateTime(item.end_time)}`}
          {item.location && ` · ${item.location}`}
        </div>
        {item.description && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            {item.description}
          </div>
        )}
      </div>
      <div className="list-item-actions">
        <button onClick={() => onEdit(item)} className="btn btn-secondary btn-sm">Edit</button>
        <button onClick={() => onDelete(item.id)} className="btn btn-danger btn-sm">Delete</button>
      </div>
    </div>
  );
}

function Appointments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      const res = await appointmentsAPI.getAll();
      setItems(res.data);
    } catch (err) {
      console.error('Error fetching appointments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) await appointmentsAPI.update(editingId, formData);
      else await appointmentsAPI.create(formData);
      fetchItems();
      resetForm();
    } catch (err) {
      console.error('Error saving appointment:', err);
      alert('Failed to save appointment');
    }
  };

  const handleEdit = (item) => {
    setFormData({
      title: item.title,
      description: item.description || '',
      appointment_date: toInputDateTime(item.appointment_date),
      end_time: item.end_time ? toInputDateTime(item.end_time) : '',
      location: item.location || '',
      category: item.category || '',
      notes: item.notes || '',
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this appointment?')) {
      try { await appointmentsAPI.delete(id); fetchItems(); }
      catch (err) { console.error(err); }
    }
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
  };

  const set = (field) => (e) => setFormData({ ...formData, [field]: e.target.value });

  const now = new Date();
  const upcoming = items.filter(i => new Date(i.appointment_date) >= now);
  const past = items.filter(i => new Date(i.appointment_date) < now);

  if (loading) return <div className="loading"><div className="spinner"></div></div>;

  return (
    <div className="container fade-in">
      <div className="page-header">
        <div>
          <h2>Appointments</h2>
          <p>Track family appointments and events</p>
        </div>
        <button onClick={() => { if (showForm && !editingId) resetForm(); else setShowForm(!showForm); }}
          className="btn btn-primary">
          {showForm ? 'Cancel' : '+ Add Appointment'}
        </button>
      </div>

      <div className="card">
        {showForm && (
          <div className="form-section">
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Title *</label>
                <input type="text" className="form-input" value={formData.title}
                  onChange={set('title')} placeholder="Appointment title" required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Date &amp; Time *</label>
                  <input type="datetime-local" className="form-input"
                    value={formData.appointment_date} onChange={set('appointment_date')} required />
                </div>
                <div className="form-group">
                  <label className="form-label">End Time</label>
                  <input type="datetime-local" className="form-input"
                    value={formData.end_time} onChange={set('end_time')} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select className="form-select" value={formData.category} onChange={set('category')}>
                    <option value="">Select category</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Location</label>
                  <input type="text" className="form-input" value={formData.location}
                    onChange={set('location')} placeholder="Address or place name" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" value={formData.description}
                  onChange={set('description')} placeholder="Optional notes..." />
              </div>
              <div className="flex gap-1">
                <button type="submit" className="btn btn-success">
                  {editingId ? 'Update' : 'Add'}
                </button>
                <button type="button" onClick={resetForm} className="btn btn-secondary">Cancel</button>
              </div>
            </form>
          </div>
        )}

        {items.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
            </div>
            <p className="empty-state-text">No appointments yet</p>
            <button onClick={() => setShowForm(true)} className="btn btn-primary">
              Add Your First Appointment
            </button>
          </div>
        ) : (
          <>
            {upcoming.length > 0 && (
              <div>
                <div style={{
                  padding: '12px 24px 6px', fontSize: 12, fontWeight: 700,
                  color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em',
                }}>
                  Upcoming ({upcoming.length})
                </div>
                {upcoming.map(item => (
                  <AppointmentItem key={item.id} item={item} onEdit={handleEdit} onDelete={handleDelete} />
                ))}
              </div>
            )}
            {past.length > 0 && (
              <div>
                <div style={{
                  padding: '16px 24px 6px', fontSize: 12, fontWeight: 700,
                  color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em',
                }}>
                  Past ({past.length})
                </div>
                {past.map(item => (
                  <AppointmentItem key={item.id} item={item} onEdit={handleEdit} onDelete={handleDelete} past />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Appointments;
